import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export type FontSize = 'large' | 'medium' | 'small' | 'very-small';

const FONT_SIZE_STORAGE_KEY = 'hop-studio-font-size';

const FONT_SIZE_VALUES: Record<FontSize, string> = {
  'large': '18px',
  'medium': '16px',
  'small': '14px',
  'very-small': '12px',
};

const DEFAULT_FONT_SIZE: FontSize = 'medium';

interface FontSizeContextValue {
  fontSize: FontSize;
  setFontSize: (size: FontSize) => void;
  fontSizeValue: string;
  fontSizeNumber: number;
}

const FontSizeContext = createContext<FontSizeContextValue | undefined>(undefined);

interface FontSizeProviderProps {
  children: React.ReactNode;
}

export const FontSizeProvider: React.FC<FontSizeProviderProps> = ({ children }) => {
  const [fontSize, setFontSizeState] = useState<FontSize>(() => {
    const saved = localStorage.getItem(FONT_SIZE_STORAGE_KEY);
    if (saved && (saved === 'large' || saved === 'medium' || saved === 'small' || saved === 'very-small')) {
      return saved as FontSize;
    }
    return DEFAULT_FONT_SIZE;
  });

  const applyFontSize = useCallback((size: FontSize) => {
    const root = document.documentElement;
    const fontSizeValue = FONT_SIZE_VALUES[size];
    root.style.setProperty('--font-size-base', fontSizeValue);
    
    // Also apply to body for global font size
    document.body.style.fontSize = fontSizeValue;
  }, []);

  const setFontSize = useCallback((size: FontSize) => {
    setFontSizeState(size);
    localStorage.setItem(FONT_SIZE_STORAGE_KEY, size);
    applyFontSize(size);
  }, [applyFontSize]);

  // Apply font size on mount and when it changes
  useEffect(() => {
    applyFontSize(fontSize);
  }, [fontSize, applyFontSize]);

  const contextValue: FontSizeContextValue = {
    fontSize,
    setFontSize,
    fontSizeValue: FONT_SIZE_VALUES[fontSize],
    fontSizeNumber: parseInt(FONT_SIZE_VALUES[fontSize]),
  };

  return (
    <FontSizeContext.Provider value={contextValue}>
      {children}
    </FontSizeContext.Provider>
  );
};

export const useFontSize = (): FontSizeContextValue => {
  const context = useContext(FontSizeContext);
  if (context === undefined) {
    throw new Error('useFontSize must be used within a FontSizeProvider');
  }
  return context;
};

